import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { Ts10Component, Ts10Summary } from '../types';

/**
 * TS Lot 1 §10.1 / §10.2 — the contractual score, and the page's headline.
 *
 *   Result_Coefficient = K1_norm × 0.4 + K2_norm × 0.4 + K3_norm × 0.2
 *   Bonus              = Base salary × Target % × Result_Coefficient
 *
 * Built to the display rules the client set out in the 2026-07-26 remarks:
 *   - **D5** the formula, the weights and each component's contribution are on
 *     the page — a score that decides money has to be checkable by the person
 *     it applies to.
 *   - **D1** every value shows the counts behind it (QA scores, NPS responses,
 *     reviews) rather than a bare number.
 *   - **D2/A2/A3** when any weighted input is missing the coefficient is *not*
 *     computed: the card says which component is missing and what to do about it.
 *   - **D6** empty values render as an em dash, never an internal placeholder.
 */

const DASH = '—';

function fmtValue(c: Ts10Component): string {
  if (c.value == null) return DASH;
  if (c.key === 'K2') return `${c.value > 0 ? '+' : ''}${c.value}`;
  return `${c.value} / ${c.scaleMax}`;
}

/** The counts a component was computed from, as a short readable line. */
function evidenceLine(
  c: Ts10Component,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const e = c.evidence ?? {};
  if (c.key === 'K1') return t('KPI.ts10.k1Evidence', { scores: Number(e.scores ?? 0), matters: Number(e.matters ?? 0) });
  if (c.key === 'K2')
    return t('KPI.ts10.k2Evidence', {
      responses: Number(e.responses ?? 0),
      promoters: Number(e.promoters ?? 0),
      detractors: Number(e.detractors ?? 0),
    });
  return t('KPI.ts10.k3Evidence', { reviews: Number(e.reviews ?? 0) });
}

/** Scale range per component, exactly as the TS states it (§10.2). */
const SCALE_TEXT: Record<Ts10Component['key'], string> = {
  K1: '0 – 5.1',
  K2: '−100 – +100',
  K3: '1 – 5',
};

/** Normalisation formula per component (§10.2). */
const NORM_TEXT: Record<Ts10Component['key'], string> = {
  K1: 'min(1; K1 / 5)',
  K2: '(NPS + 100) / 200',
  K3: '(K3 − 1) / 4',
};

/**
 * One input card. The target design (§3.3) requires each card to let the reader
 * reproduce the number without asking anyone: value on its own scale, the
 * normalised value, the coverage it rests on, the scale, the normalisation
 * formula and the source.
 */
function ComponentCard({ c }: { c: Ts10Component }) {
  const { t } = useTranslation('common');
  const missing = c.norm == null;

  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm',
        missing ? 'border-slate-200' : 'border-slate-300',
      )}
    >
      {/* Header: code + name on the left, fixed weight badge on the right. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[0.9375rem] font-bold text-slate-900">{c.key}</span>
          <span className="ml-1.5 text-[0.8125rem] text-slate-500">
            {t(`KPI.ts10.${c.key.toLowerCase()}Label`)}
          </span>
        </div>
        <span className="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
          {t('KPI.ts10.weight', { weight: c.weight.toFixed(2) })}
        </span>
      </div>

      {/* Value — the one thing that should be readable at a glance. */}
      <div
        className={cn(
          'mt-3 font-bold leading-none tabular-nums',
          missing ? 'text-lg text-slate-400' : 'text-[1.75rem] text-slate-900',
        )}
      >
        {missing ? t('KPI.ts10.noData') : fmtValue(c)}
      </div>

      {/* Normalised value is hidden when there is nothing to normalise. */}
      {!missing && (
        <div className="mt-2 inline-flex w-fit items-baseline gap-1.5 rounded-md bg-sky-50 px-2 py-1 text-[11px] font-medium tabular-nums text-sky-800">
          <span>{t('KPI.ts10.normalised', { value: c.norm!.toFixed(2) })}</span>
          <span className="text-sky-400">·</span>
          <span>
            {t('KPI.ts10.contribution', {
              value: (c.norm! * c.weight).toFixed(3),
            })}
          </span>
        </div>
      )}

      <p className="mt-2 text-[11px] leading-snug text-slate-500">
        {evidenceLine(c, t)}
      </p>

      {/* Reference block — scale, normalisation and source, per §3.3. */}
      <div className="mt-auto space-y-1 border-t border-slate-100 pt-2.5 text-[10.5px] leading-snug text-slate-400">
        <p className="tabular-nums">
          {t('KPI.ts10.scale', { range: SCALE_TEXT[c.key] })}
        </p>
        <p>
          {t('KPI.ts10.norm')}{' '}
          <span className="font-mono">{NORM_TEXT[c.key]}</span>
        </p>
        <p>{t(`KPI.ts10.${c.key.toLowerCase()}Source`)}</p>
        {/* R-3: the iteration-aggregation rule behind Total_Penalty / EXC_Bonus
            is not fixed in the TS yet and must not be invented — say so rather
            than let a silent 0 look like "no penalties were applied".
            ⚠ The note used to cite "(R4)", which was our own numbering. Finding
            K-08 of AL-2026/08-04-01 asked us to adopt the client's: in
            AL-2026/08-03-04 the Total_Penalty decision is **R-3** (R-4 is the
            hours norm and the revenue target). */}
        {c.key === 'K1' && c.evidence?.penaltySourced === false && (
          <p className="rounded bg-amber-50 px-1.5 py-1 text-amber-700">
            {t('KPI.ts10.penaltyNoSource')}
          </p>
        )}
      </div>
    </div>
  );
}

export function ResultCoefficientCard({
  ts10,
  isMd,
  firmMode,
}: {
  ts10: Ts10Summary | undefined;
  /** Money (bonus, base salary) is MD-only, matching the backend gate. */
  isMd: boolean;
  /** At firm scope the bonus is a per-person figure and is not shown (§3.4). */
  firmMode: boolean;
}) {
  const { t } = useTranslation('common');

  if (!ts10) return null;

  const { resultCoefficient, insufficientData, missing, bonus } = ts10;
  const pct = resultCoefficient != null ? Math.round(resultCoefficient * 100) : 0;
  const tone =
    resultCoefficient == null
      ? '#cbd5e1'
      : pct >= 75
        ? '#16a34a'
        : pct >= 50
          ? '#f59e0b'
          : '#ef4444';

  return (
    <div className="rounded-2xl bg-white p-6 drop-shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {t('KPI.ts10.title')}
          </h2>
          <p className="mt-0.5 text-[0.8125rem] text-slate-500">
            {t('KPI.ts10.subtitle')}
          </p>
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
          {t('KPI.ts10.clause')}
        </span>
      </div>

      {/* Coefficient — blocked, never substituted, while any input is empty. */}
      <div className="flex flex-wrap items-center gap-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
        {resultCoefficient != null ? (
          <div
            className="relative flex h-32 w-32 shrink-0 items-center justify-center rounded-full"
            style={{
              background: `conic-gradient(${tone} 0% ${pct}%, #e2e8f0 ${pct}% 100%)`,
            }}
          >
            <div className="absolute inset-[12px] rounded-full bg-white" />
            <div className="relative flex flex-col items-center gap-0.5">
              <div className="text-[2rem] font-bold leading-none tabular-nums text-slate-900">
                {resultCoefficient.toFixed(2)}
              </div>
              <div className="text-[0.625rem] font-semibold uppercase tracking-wider text-slate-500">
                {t('KPI.ts10.coefficient')}
              </div>
            </div>
          </div>
        ) : (
          /* Blocked state: a hollow ring reads as "zero". A plain dashed tile
             with the words reads as "we did not compute this", which is the
             message rule D2 actually requires. */
          <div className="flex h-32 w-32 shrink-0 flex-col items-center justify-center gap-1 rounded-full border-2 border-dashed border-slate-300 bg-white px-3 text-center">
            <span className="text-2xl font-bold leading-none text-slate-300">
              {DASH}
            </span>
            <span className="text-[11px] font-semibold leading-tight text-slate-500">
              {t('KPI.ts10.notCalculated')}
            </span>
          </div>
        )}

        <div className="min-w-[240px] flex-1">
          {/* Formula + weights are always on screen, calculated or not (D5). */}
          <p className="font-mono text-[0.8125rem] text-slate-700">
            K = K1norm × 0.40 + K2norm × 0.40 + K3norm × 0.20
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            {t('KPI.ts10.weightsFixed')}
          </p>
          {insufficientData && (
            <div className="mt-2.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <Info size={14} className="mt-0.5 shrink-0 text-amber-600" />
              <div className="text-[0.8125rem] text-amber-900">
                <p className="font-semibold">
                  {t('KPI.ts10.inputsMissing', {
                    missing: missing.length,
                    total: 3,
                  })}
                </p>
                <p className="mt-0.5 text-amber-800">
                  {t('KPI.ts10.notEnoughDataDetail', {
                    missing: missing.join(', '),
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Three input cards — each reproducible on its own. */}
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <ComponentCard c={ts10.k1} />
        <ComponentCard c={ts10.k2} />
        <ComponentCard c={ts10.k3} />
      </div>

      {/* Bonus — per-person, so it is not shown at firm scope (§3.4). */}
      {isMd && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span className="text-[0.8125rem] font-medium text-slate-600">
              {t('KPI.ts10.bonus')}
            </span>
            <span className="text-sm font-bold tabular-nums text-slate-900">
              {firmMode
                ? DASH
                : bonus?.amount != null
                  ? `$${bonus.amount.toLocaleString()}`
                  : DASH}
            </span>
          </div>
          <p className="mt-1 font-mono text-[11px] text-slate-500">
            Bonus = Base salary × Target % × K
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {firmMode
              ? t('KPI.ts10.bonusFirmScope')
              : bonus?.baseSalary != null && bonus?.targetPercent != null
                ? t('KPI.ts10.bonusBasis', {
                    salary: `$${bonus.baseSalary.toLocaleString()}`,
                    percent: Math.round((bonus.targetPercent ?? 0) * 100),
                    coefficient:
                      resultCoefficient != null
                        ? resultCoefficient.toFixed(2)
                        : DASH,
                  })
                : t('KPI.ts10.bonusNoBasis')}
          </p>
        </div>
      )}
    </div>
  );
}
